import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "./ui/dialog";
import { assignments as assignmentsApi } from "../lib/api";
import { localizedTemplate, localizedCategory } from "../lib/i18nContent";
import { STATUS_META, fmtDate } from "../lib/assignmentMeta";
import {
  ClipboardList, ClipboardCheck, AlertTriangle, Store as StoreIcon, Loader2, ArrowRight,
} from "lucide-react";

export default function StoreHistoryDialog({ open, onOpenChange, store }) {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open || !store?.id) return;
    setLoading(true);
    assignmentsApi
      .storeHistory(store.id)
      .then(setData)
      .catch(() => setData({ audits: [], findings: [], assignments: [] }))
      .finally(() => setLoading(false));
  }, [open, store]);

  const statusLabel = (s) => t((STATUS_META[s] || STATUS_META.pending).key);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="store-history-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl flex items-center gap-2">
            <StoreIcon size={20} className="text-[#1E3A8A]" />
            {t("assignments.store_history")}
          </DialogTitle>
          <p className="text-sm text-stone-500">{store?.tienda} · {store?.ciudad}</p>
        </DialogHeader>

        {loading || !data ? (
          <div className="p-10 text-center text-stone-400"><Loader2 className="animate-spin inline" size={18} /></div>
        ) : (
          <div className="space-y-6 mt-2">
            {/* Assignments */}
            <section data-testid="history-assignments">
              <div className="canales-overline mb-2 flex items-center gap-1.5"><ClipboardCheck size={13} /> {t("nav.assignments")} · {data.assignments.length}</div>
              {data.assignments.length === 0 ? (
                <p className="text-xs text-stone-400">—</p>
              ) : (
                <div className="space-y-1.5">
                  {data.assignments.map((a) => (
                    <button
                      key={a.id}
                      onClick={() => { onOpenChange(false); navigate(`/assignments/${a.id}`); }}
                      className="w-full text-left flex items-center justify-between gap-3 rounded-lg border border-stone-200 p-2.5 hover:border-[#1E3A8A] transition-colors"
                      data-testid={`history-assignment-${a.id}`}
                    >
                      <div className="min-w-0">
                        <div className="text-sm font-medium truncate">
                          {a.type === "task_checklist" ? (a.title || t("assignments.type_task_checklist")) : (a.categories || []).map((c) => localizedTemplate(c, i18n.language)).join(", ")}
                        </div>
                        <div className="text-[11px] text-stone-500">{a.auditor_name} · {a.due_date ? fmtDate(a.due_date, i18n.language) : t("assignments.no_due")}</div>
                      </div>
                      <span className="text-[11px] font-semibold text-stone-600 shrink-0">{statusLabel(a.effective_status)}</span>
                    </button>
                  ))}
                </div>
              )}
            </section>

            {/* Audits */}
            <section data-testid="history-audits">
              <div className="canales-overline mb-2 flex items-center gap-1.5"><ClipboardList size={13} /> {t("nav.audits")} · {data.audits.length}</div>
              {data.audits.length === 0 ? (
                <p className="text-xs text-stone-400">—</p>
              ) : (
                <div className="space-y-1.5">
                  {data.audits.map((au) => (
                    <button
                      key={au.id}
                      onClick={() => { onOpenChange(false); navigate(`/summary/${au.id}`); }}
                      className="w-full text-left flex items-center justify-between gap-3 rounded-lg border border-stone-200 p-2.5 hover:border-[#1E3A8A] transition-colors"
                    >
                      <div className="min-w-0">
                        <div className="text-sm font-medium truncate">{au.audit_number} · {au.auditor_name}</div>
                        <div className="text-[11px] text-stone-500">
                          {(au.templates || [au.template]).map((c) => localizedTemplate(c, i18n.language)).join(", ")} · {(au.started_at || "").slice(0, 10)}
                        </div>
                      </div>
                      <div className="flex items-center gap-2 shrink-0">
                        {au.score != null && <span className="text-sm font-bold text-[#1E3A8A]">{Math.round(au.score)}%</span>}
                        <ArrowRight size={13} className="text-stone-400" />
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </section>

            {/* Findings */}
            <section data-testid="history-findings">
              <div className="canales-overline mb-2 flex items-center gap-1.5"><AlertTriangle size={13} /> {t("nav.findings")} · {data.findings.length}</div>
              {data.findings.length === 0 ? (
                <p className="text-xs text-stone-400">—</p>
              ) : (
                <div className="space-y-1.5 max-h-56 overflow-y-auto pr-1">
                  {data.findings.map((f) => (
                    <div key={f.id} className="flex items-center justify-between gap-3 rounded-lg border border-stone-200 p-2.5">
                      <div className="min-w-0">
                        <div className="text-sm font-medium truncate">{f.description}</div>
                        <div className="text-[11px] text-stone-500">{localizedCategory(f.category, i18n.language)}</div>
                      </div>
                      <span className="text-[11px] font-semibold text-stone-600 shrink-0">{f.status}</span>
                    </div>
                  ))}
                </div>
              )}
            </section>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
